import { Link } from "react-router-dom";
import { Check, Mail, Calendar, Heart } from "lucide-react";
import { IMG } from "@/lib/site";
import Reveal from "@/components/site/Reveal";
import NewsletterSignup from "@/components/site/NewsletterSignup";
import SubscriberCount from "@/components/site/SubscriberCount";
import useSeo from "@/lib/useSeo";

const benefits = [
  {
    icon: Calendar,
    title: "Una al mes",
    body: "Ni un email más, ni uno menos. Sin saturarte el buzón.",
  },
  {
    icon: Heart,
    title: "Reflexión + recurso + práctica",
    body: "Una idea para pensar, un recurso útil y una práctica concreta cuerpo-mente que puedes aplicar.",
  },
  {
    icon: Mail,
    title: "Sin spam, sin venta",
    body: "Nunca te venderé nada. Si en algún momento quiero ofrecer algo, será suave y opcional.",
  },
  {
    icon: Check,
    title: "Baja en un clic",
    body: "Si en algún momento no te aporta, te das de baja respondiendo 'baja' y se acabó.",
  },
];

const Newsletter = () => {
  useSeo({
    title: {
      es: "Newsletter Mensual · Acompañamiento Cuerpo-Mente | Método TAO",
      ca: "Newsletter Mensual · Acompanyament Cos-Ment | Mètode TAO",
    },
    description: {
      es: "Suscríbete a la newsletter mensual de Método TAO. Una vez al mes recibes una reflexión, un recurso útil y una práctica cuerpo-mente para procesos oncológicos, fibromialgia y fatiga crónica. Gratis, sin spam.",
      ca: "Subscriu-te a la newsletter mensual del Mètode TAO. Un cop al mes reps una reflexió, un recurs útil i una pràctica cos-ment per a processos oncològics, fibromiàlgia i fatiga crònica. Gratis, sense spam.",
    },
    path: "/newsletter",
  });

  return (
    <div className="bg-ivory" data-testid="newsletter-page">
      {/* HERO */}
      <section className="pt-12 md:pt-20 pb-16 md:pb-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <Reveal className="lg:col-span-7">
            <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-6">Newsletter mensual</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-charcoal leading-[1.05]">
              Una vez al mes, algo que te <span className="italic text-terracotta">sostenga</span>.
            </h1>
            <p className="mt-7 text-lg md:text-xl leading-relaxed text-charcoal/75 font-light max-w-xl">
              Si estás atravesando un proceso oncológico, fibromialgia o fatiga crónica —o acompañas a alguien que lo está atravesando— suscríbete a la newsletter. Una reflexión, un recurso útil y una práctica concreta cuerpo-mente. Cada mes, directo a tu email.
            </p>
            <div className="mt-7">
              <SubscriberCount variant="hero" />
            </div>
            <ul className="mt-8 space-y-2">
              <li className="flex items-center gap-2.5 text-[14.5px] text-charcoal/75">
                <Check size={15} className="text-olive shrink-0" /> Sin spam · Sin venderte nada
              </li>
              <li className="flex items-center gap-2.5 text-[14.5px] text-charcoal/75">
                <Check size={15} className="text-olive shrink-0" /> Te puedes dar de baja con un clic
              </li>
              <li className="flex items-center gap-2.5 text-[14.5px] text-charcoal/75">
                <Check size={15} className="text-olive shrink-0" /> Email de bienvenida en cuanto te suscribes
              </li>
            </ul>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="aspect-[4/5] rounded-tr-[4rem] rounded-bl-[4rem] overflow-hidden bg-sand">
              <img src="https://customer-assets.emergentagent.com/job_acompanamiento-tao/artifacts/jzsvdpdw_compasion.png" alt="Newsletter Método TAO · Autocompasión" className="w-full h-full object-cover" fetchPriority="high" decoding="async" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FORM SECTION */}
      <section className="py-12 md:py-16">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <Reveal>
            <NewsletterSignup variant="light" source="newsletter-page" hideSocialProof />
          </Reveal>
        </div>
      </section>

      {/* WHAT TO EXPECT */}
      <section className="py-20 md:py-28 bg-sand/30 grain">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-5">Qué vas a recibir</p>
            <h2 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.05]">
              Cuidada, breve, <span className="italic">sin ruido</span>.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-6 lg:gap-7">
            {benefits.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.06}>
                <article className="bg-ivory rounded-3xl p-7 border border-sand/60 h-full">
                  <span className="w-11 h-11 rounded-full bg-sand flex items-center justify-center text-olive">
                    <b.icon size={18} />
                  </span>
                  <h3 className="mt-5 font-serif text-xl text-charcoal">{b.title}</h3>
                  <p className="mt-2 text-charcoal/65 leading-relaxed text-[14px]">{b.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SAMPLE PREVIEW — first newsletter in their inbox */}
      <section className="py-20 md:py-28" data-testid="newsletter-sample">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <Reveal className="max-w-2xl mb-12">
            <p className="text-[11px] tracking-[0.32em] uppercase text-terracotta mb-5">
              Una muestra
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.05]">
              La primera que recibirás <span className="italic">al suscribirte</span>.
            </h2>
            <p className="mt-5 text-charcoal/70 font-light leading-relaxed">
              Te llega al email en cuanto te suscribes. Es el manifiesto fundacional:
              de dónde nace el Método TAO y por qué lo hago.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            {/* Mock email envelope */}
            <div className="bg-white rounded-3xl overflow-hidden border border-sand/50 shadow-2xl shadow-charcoal/10 max-w-[680px] mx-auto">
              {/* Email header */}
              <div className="px-7 py-5 border-b border-sand/40 bg-cream/40">
                <div className="flex items-center gap-3 text-xs text-charcoal/55 mb-3">
                  <span className="w-8 h-8 rounded-full bg-charcoal text-ivory grid place-items-center font-serif text-sm">M</span>
                  <div>
                    <p className="text-charcoal font-medium">Marta Montardit · Método TAO</p>
                    <p className="text-[11px] tracking-wider uppercase">marta@metodo-tao.com</p>
                  </div>
                </div>
                <p className="font-serif text-xl text-charcoal mt-2 leading-tight">
                  No fue el cáncer. Fue lo que vino después.
                </p>
                <p className="text-[13px] text-charcoal/55 mt-1.5 italic">
                  Lo que aprendí cuando el tratamiento terminó y nadie sabía qué hacer conmigo.
                </p>
              </div>

              {/* Email body excerpt */}
              <div className="px-7 py-7 text-[15px] text-charcoal/85 leading-[1.75] font-serif">
                <p className="italic text-charcoal">Hola amiga,</p>
                <p className="mt-4">
                  Si estás aquí es porque algo del Método TAO te ha tocado.
                  Antes de seguir escribiéndote, quiero contarte de dónde nace
                  todo esto. Sin marketing. Sin retórica. Solo lo que pasó.
                </p>
                <p className="mt-4">
                  <strong>Pasé por un cáncer.</strong> Y lo que aprendí —lo que
                  de verdad me cambió por dentro— no fue el diagnóstico ni el
                  tratamiento. Fue <strong>lo que vino después</strong>.
                </p>
                <p className="mt-4">
                  Cuando termina el tratamiento, te dan el alta y te dicen que
                  ya estás bien. Pero tú no te sientes bien…
                </p>
                <div className="mt-6 pl-5 border-l-2 border-terracotta italic text-charcoal/75 text-[15.5px] leading-relaxed">
                  Descubrí algo que lo cambió todo: no estaba rota. Estaba
                  desregulada. Mi sistema nervioso llevaba años en estado de
                  alerta.<br />Mi cuerpo no me había traicionado: me estaba
                  protegiendo.
                </div>
                <p className="mt-6 text-charcoal/55 text-sm not-italic">
                  [continúa en tu email…]
                </p>
              </div>
            </div>

            <p className="text-center mt-8 text-sm text-charcoal/55 max-w-md mx-auto">
              Sin spam · Sin presión por comprar nada · Te puedes dar de baja
              respondiendo "baja" en cualquier momento.
            </p>
          </Reveal>
        </div>
      </section>

      {/* TRUST + SIGNATURE */}
      <section className="py-20 md:py-28">
        <div className="max-w-[800px] mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <img
              src="https://customer-assets.emergentagent.com/job_acompanamiento-tao/artifacts/yoox5k3i_WhatsApp%20Image%202026-01-12%20at%2009.48.06.jpeg"
              alt="Marta Montardit"
              className="w-24 h-24 rounded-full object-cover mx-auto"
              loading="lazy"
              decoding="async"
            />
            <p className="mt-8 font-serif italic text-2xl md:text-3xl text-charcoal leading-[1.35]">
              "Atravesé mi propio proceso oncológico y desde ahí nació Método TAO.
              Esta newsletter es lo que ojalá alguien me hubiera mandado a mí cada mes."
            </p>
            <p className="mt-6 text-[11.5px] tracking-[0.32em] uppercase text-charcoal/55">
              Marta Montardit · Método TAO
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/blog"
                data-testid="newsletter-blog-link"
                className="inline-flex items-center justify-center gap-2 border border-charcoal/30 text-charcoal rounded-full px-7 py-4 text-sm font-medium hover:bg-charcoal hover:text-ivory transition-all"
              >
                Mientras tanto, ir al blog
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Newsletter;
